import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import Select from 'react-select';
import api from '../services/api';
import { formatMoney } from '../utils/currency';
import Modal from '../components/common/Modal';
import StatusBadge from '../components/common/StatusBadge';
import LoadingSpinner from '../components/common/LoadingSpinner';

const ACTIVE_FOR_CANCEL  = ['scheduled', 'pending', 'accepted', 'arriving', 'arrived'];
const ACTIVE_FOR_ACTIONS = ['accepted', 'arriving', 'arrived', 'in_progress'];

const RideDetails = () => {
  const { id } = useParams();
  const [ride, setRide] = useState(null);
  const [currencyCode, setCurrencyCode] = useState('QAR');
  const [loading, setLoading] = useState(true);

  // Modal state
  const [activeModal, setActiveModal] = useState(null); // 'cancel'|'reassign'|'force'|'refund'
  const [submitting, setSubmitting] = useState(false);

  // Cancel form
  const [cancelReason, setCancelReason] = useState('');
  const [customerRequested, setCustomerRequested] = useState(false);

  // Reassign form
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [reassignReason, setReassignReason] = useState('');

  // Refund form
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');

  useEffect(() => {
    fetchRideDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchRideDetails = async () => {
    try {
      const response = await api.get(`/admin/rides/${id}`);
      setRide(response.data.data);
      setCurrencyCode(String(response.data?.currencyCode || 'QAR').toUpperCase());
    } catch (error) {
      console.error('Failed to fetch ride details');
    } finally {
      setLoading(false);
    }
  };

  const openModal = async (modal) => {
    setActiveModal(modal);

    if (modal === 'reassign') {
      setCandidatesLoading(true);
      setSelectedDriver(null);
      setReassignReason('');
      try {
        const res = await api.get(`/admin/rides/${id}/reassign-candidates`);
        const drivers = res.data.data || [];
        setCandidates(
          drivers.map((d) => ({
            value: d._id,
            label: [
              `${d.firstName} ${d.lastName}`,
              d.rating ? `⭐ ${Number(d.rating).toFixed(1)}` : null,
              d.vehicle?.make ? `${d.vehicle.make} ${d.vehicle.model || ''}`.trim() : null,
            ]
              .filter(Boolean)
              .join(' · '),
          }))
        );
      } catch {
        toast.error('Failed to load available drivers');
        setActiveModal(null);
      } finally {
        setCandidatesLoading(false);
      }
    }
  };

  const closeModal = () => {
    if (submitting) return;
    setActiveModal(null);
    setCancelReason('');
    setCustomerRequested(false);
    setSelectedDriver(null);
    setReassignReason('');
    setRefundAmount('');
    setRefundReason('');
  };

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      await api.post(`/admin/rides/${id}/cancel`, {
        reason: cancelReason || undefined,
        customerRequested,
      });
      toast.success(
        customerRequested
          ? 'Ride cancelled — cancellation fee applied'
          : 'Ride cancelled — refund initiated if applicable'
      );
      closeModal();
      fetchRideDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel ride');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedDriver) {
      toast.error('Please select a driver');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/admin/rides/${id}/reassign`, {
        driverId: selectedDriver.value,
        reason: reassignReason || undefined,
      });
      toast.success('Ride reassigned successfully');
      closeModal();
      fetchRideDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reassign ride');
    } finally {
      setSubmitting(false);
    }
  };

  const handleForceComplete = async () => {
    setSubmitting(true);
    try {
      await api.post(`/admin/rides/${id}/force-complete`);
      toast.success('Ride force-completed — billing initiated');
      closeModal();
      fetchRideDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to force-complete ride');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefund = async () => {
    setSubmitting(true);
    try {
      const body = {};
      if (refundAmount) body.amount = parseFloat(refundAmount);
      if (refundReason) body.reason = refundReason;
      await api.post(`/admin/rides/${id}/refund`, body);
      toast.success('Refund processed successfully');
      closeModal();
      fetchRideDetails();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to process refund');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!ride) {
    return <div className="text-center py-12">Ride not found</div>;
  }

  const canCancel   = ACTIVE_FOR_CANCEL.includes(ride.status);
  const canReassign = ACTIVE_FOR_ACTIONS.includes(ride.status);
  const canForce    = ACTIVE_FOR_ACTIONS.includes(ride.status);
  const canRefund   = ride.paymentStatus === 'completed';
  const hasActions  = canCancel || canReassign || canForce || canRefund;

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/rides" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeftIcon className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">Ride #{ride._id.slice(-6)}</h1>
          <StatusBadge status={ride.status} />
        </div>

        {hasActions && (
          <div className="flex items-center gap-3">
            {canForce && (
              <button onClick={() => openModal('force')} className="btn-primary text-sm">
                Force Complete
              </button>
            )}
            {canReassign && (
              <button onClick={() => openModal('reassign')} className="btn-secondary text-sm">
                Reassign Driver
              </button>
            )}
            {canRefund && (
              <button
                onClick={() => openModal('refund')}
                className="btn-secondary text-sm text-red-600 border-red-200 hover:bg-red-50"
              >
                Refund
              </button>
            )}
            {canCancel && (
              <button onClick={() => openModal('cancel')} className="btn-danger text-sm">
                Cancel Ride
              </button>
            )}
          </div>
        )}
      </div>

      {/* Route + Fare grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Route Info */}
        <div className="card lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Route Details</h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <div className="w-3 h-3 bg-green-500 rounded-full mt-1.5" />
              <div>
                <p className="text-sm text-gray-500">Pickup</p>
                <p className="font-medium">{ride.pickup?.address}</p>
              </div>
            </div>
            <div className="ml-1.5 border-l-2 border-dashed border-gray-300 h-8" />
            <div className="flex items-start space-x-3">
              <div className="w-3 h-3 bg-red-500 rounded-full mt-1.5" />
              <div>
                <p className="text-sm text-gray-500">Dropoff</p>
                <p className="font-medium">{ride.dropoff?.address}</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
            <div className="text-center">
              <p className="text-2xl font-bold text-indigo-600">{ride.distance?.toFixed(1)} km</p>
              <p className="text-sm text-gray-500">Distance</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-indigo-600">{ride.actualDuration ?? ride.duration} min</p>
              <p className="text-sm text-gray-500">Duration</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-indigo-600 capitalize">{ride.rideType}</p>
              <p className="text-sm text-gray-500">Ride Type</p>
            </div>
          </div>
        </div>

        {/* Fare Details */}
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Fare Breakdown</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Base Fare</span>
              <span>{formatMoney(ride.fare?.baseFare || 0, ride.fare?.currency || currencyCode || 'QAR')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Distance Fare</span>
              <span>{formatMoney(ride.fare?.distanceFare || 0, ride.fare?.currency || currencyCode || 'QAR')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Time Fare</span>
              <span>{formatMoney(ride.fare?.timeFare || 0, ride.fare?.currency || currencyCode || 'QAR')}</span>
            </div>
            {ride.fare?.surgeFare > 0 && (
              <div className="flex justify-between text-yellow-600">
                <span>Surge Fare</span>
                <span>{formatMoney(ride.fare?.surgeFare || 0, ride.fare?.currency || currencyCode || 'QAR')}</span>
              </div>
            )}
            <div className="flex justify-between pt-3 border-t font-bold text-lg">
              <span>Total</span>
              <span className="text-green-600">{formatMoney(ride.fare?.totalFare || 0, ride.fare?.currency || currencyCode || 'QAR')}</span>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Payment Method</span>
              <span className="capitalize">{ride.paymentMethod}</span>
            </div>
            <div className="flex justify-between text-sm mt-2">
              <span className="text-gray-500">Payment Status</span>
              <StatusBadge status={ride.paymentStatus} />
            </div>
          </div>
        </div>
      </div>

      {/* Rider + Driver */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Rider</h3>
          {ride.user ? (
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-bold">
                  {ride.user.firstName?.[0]}{ride.user.lastName?.[0]}
                </span>
              </div>
              <div>
                <p className="font-medium">{ride.user.firstName} {ride.user.lastName}</p>
                <p className="text-sm text-gray-500">{ride.user.phone}</p>
                <p className="text-sm text-gray-500">{ride.user.email}</p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">User information not available</p>
          )}
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold mb-4">Driver</h3>
          {ride.driver ? (
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 font-bold">
                  {ride.driver.firstName?.[0]}{ride.driver.lastName?.[0]}
                </span>
              </div>
              <div>
                <p className="font-medium">{ride.driver.firstName} {ride.driver.lastName}</p>
                <p className="text-sm text-gray-500">{ride.driver.phone}</p>
                <p className="text-sm text-gray-500">
                  {ride.driver.vehicle?.make} {ride.driver.vehicle?.model} • {ride.driver.vehicle?.licensePlate}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">No driver assigned</p>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4">Timeline</h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="w-2 h-2 bg-green-500 rounded-full" />
            <span className="text-sm text-gray-500 w-32">Requested</span>
            <span className="text-sm">{ride.requestedAt ? new Date(ride.requestedAt).toLocaleString() : '-'}</span>
          </div>
          {ride.acceptedAt && (
            <div className="flex items-center space-x-4">
              <div className="w-2 h-2 bg-blue-500 rounded-full" />
              <span className="text-sm text-gray-500 w-32">Accepted</span>
              <span className="text-sm">{new Date(ride.acceptedAt).toLocaleString()}</span>
            </div>
          )}
          {ride.arrivedAt && (
            <div className="flex items-center space-x-4">
              <div className="w-2 h-2 bg-purple-500 rounded-full" />
              <span className="text-sm text-gray-500 w-32">Driver Arrived</span>
              <span className="text-sm">{new Date(ride.arrivedAt).toLocaleString()}</span>
            </div>
          )}
          {ride.startedAt && (
            <div className="flex items-center space-x-4">
              <div className="w-2 h-2 bg-indigo-500 rounded-full" />
              <span className="text-sm text-gray-500 w-32">Trip Started</span>
              <span className="text-sm">{new Date(ride.startedAt).toLocaleString()}</span>
            </div>
          )}
          {ride.completedAt && (
            <div className="flex items-center space-x-4">
              <div className="w-2 h-2 bg-green-500 rounded-full" />
              <span className="text-sm text-gray-500 w-32">Completed</span>
              <span className="text-sm">{new Date(ride.completedAt).toLocaleString()}</span>
            </div>
          )}
          {ride.cancelledAt && (
            <div className="flex items-center space-x-4">
              <div className="w-2 h-2 bg-red-500 rounded-full" />
              <span className="text-sm text-gray-500 w-32">Cancelled</span>
              <span className="text-sm">{new Date(ride.cancelledAt).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Cancel Modal ─────────────────────────────────────────────────── */}
      <Modal
        isOpen={activeModal === 'cancel'}
        onClose={closeModal}
        title="Cancel Ride"
        footer={
          <>
            <button className="btn-secondary text-sm" onClick={closeModal} disabled={submitting}>
              Back
            </button>
            <button className="btn-danger text-sm" onClick={handleCancel} disabled={submitting}>
              {submitting ? 'Cancelling…' : 'Confirm Cancel'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              className="input text-sm resize-none"
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Describe why the ride is being cancelled…"
            />
          </div>

          <div>
            <p className="text-sm font-medium text-gray-700 mb-2">Cancellation type</p>
            <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="cancelType"
                className="mt-0.5 cursor-pointer"
                checked={customerRequested}
                onChange={() => setCustomerRequested(true)}
              />
              <div>
                <p className="text-sm font-medium text-gray-800">Customer requested</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Applies the standard cancellation fee (5 QAR if driver had arrived or was arriving). No refund.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-3 rounded-lg border mt-2 cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                name="cancelType"
                className="mt-0.5 cursor-pointer"
                checked={!customerRequested}
                onChange={() => setCustomerRequested(false)}
              />
              <div>
                <p className="text-sm font-medium text-gray-800">Operational cancel</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Waives the fee. Auto-refunds the rider if they were already charged.
                </p>
              </div>
            </label>
          </div>
        </div>
      </Modal>

      {/* ── Reassign Modal ───────────────────────────────────────────────── */}
      <Modal
        isOpen={activeModal === 'reassign'}
        onClose={closeModal}
        title="Reassign Driver"
        footer={
          <>
            <button className="btn-secondary text-sm" onClick={closeModal} disabled={submitting}>
              Back
            </button>
            <button
              className="btn-primary text-sm"
              onClick={handleReassign}
              disabled={submitting || !selectedDriver}
            >
              {submitting ? 'Reassigning…' : 'Confirm Reassign'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select driver</label>
            {candidatesLoading ? (
              <div className="flex items-center justify-center h-10">
                <LoadingSpinner size="sm" />
              </div>
            ) : (
              <Select
                options={candidates}
                value={selectedDriver}
                onChange={setSelectedDriver}
                placeholder={
                  candidates.length === 0
                    ? 'No available drivers nearby'
                    : 'Choose a driver…'
                }
                isDisabled={candidates.length === 0}
                classNamePrefix="react-select"
              />
            )}
            {!candidatesLoading && candidates.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                No online, available drivers found near the pickup location.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              className="input text-sm resize-none"
              rows={2}
              value={reassignReason}
              onChange={(e) => setReassignReason(e.target.value)}
              placeholder="e.g. Previous driver went offline"
            />
          </div>
        </div>
      </Modal>

      {/* ── Force Complete Modal ─────────────────────────────────────────── */}
      <Modal
        isOpen={activeModal === 'force'}
        onClose={closeModal}
        title="Force Complete Ride"
        footer={
          <>
            <button className="btn-secondary text-sm" onClick={closeModal} disabled={submitting}>
              Back
            </button>
            <button className="btn-primary text-sm" onClick={handleForceComplete} disabled={submitting}>
              {submitting ? 'Completing…' : 'Confirm Force Complete'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            This will mark the ride as <strong>completed</strong> and run normal billing:
          </p>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li>Rider's wallet or cash payment will be processed</li>
            <li>Driver will receive their earnings and ledger credit</li>
            <li>Driver will be freed and shown as available</li>
          </ul>
          <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3 mt-2">
            This action cannot be undone. Use only when the driver failed to complete the ride in the app.
          </p>
        </div>
      </Modal>

      {/* ── Refund Modal ─────────────────────────────────────────────────── */}
      <Modal
        isOpen={activeModal === 'refund'}
        onClose={closeModal}
        title="Refund Ride Payment"
        footer={
          <>
            <button className="btn-secondary text-sm" onClick={closeModal} disabled={submitting}>
              Back
            </button>
            <button className="btn-danger text-sm" onClick={handleRefund} disabled={submitting}>
              {submitting ? 'Processing…' : 'Issue Refund'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount ({ride.fare?.currency || currencyCode}){' '}
              <span className="text-gray-400 font-normal">— leave blank to refund in full</span>
            </label>
            <input
              type="number"
              className="input text-sm"
              min="0.01"
              step="0.01"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              placeholder={`Max: ${Number(ride.fare?.totalFare || 0).toFixed(2)}`}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              className="input text-sm resize-none"
              rows={2}
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Reason for refund…"
            />
          </div>

          <p className="text-xs text-gray-500">Refund will be credited to the rider's wallet.</p>
        </div>
      </Modal>

    </div>
  );
};

export default RideDetails;
